import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Wrench } from "lucide-react";

/**
 * Retrofit a toll-processing companion PO onto an existing draft PO.
 * Rendered from Orders.jsx when a draft PO doesn't yet have a
 * companion — pick a processor + fee-per-log and the backend spins up
 * the linked cutting PO on the fly. Kept intentionally small; the full
 * flow at draft-creation time still lives inline on the reorder
 * suggestions block.
 */
const AttachProcessorDialog = ({ open, po, processors, onClose, onAttached }) => {
  const [processorId, setProcessorId] = useState("");
  const [feePerLog, setFeePerLog] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setProcessorId("");
      setFeePerLog("");
    }
  }, [open]);

  // Total logs = sum of parent line qtys. Shown live so the manager
  // sees the resulting cutting cost before hitting Attach.
  const totalLogs = useMemo(
    () => (po?.items || []).reduce((s, it) => s + Number(it.qty || 0), 0),
    [po?.items],
  );
  const preview = useMemo(() => {
    const fee = Number(feePerLog || 0);
    if (!fee || fee <= 0 || totalLogs <= 0) return null;
    return {
      totalCost: totalLogs * fee,
      totalWheels: Math.round(totalLogs * 10),
    };
  }, [feePerLog, totalLogs]);

  if (!open || !po) return null;

  const submit = async () => {
    if (!processorId) return toast.error("Pick a processor");
    const fee = Number(feePerLog || 0);
    if (!fee || fee <= 0) return toast.error("Enter a fee per log > 0");
    setBusy(true);
    try {
      const { data } = await api.post(`/po/${po.id}/attach-processor`, {
        processor_id: processorId,
        fee_per_log: fee,
      });
      toast.success(
        `Companion ${data.companion.po_number} created`,
        {
          description: `${data.companion.supplier_snapshot?.name} · $${data.companion.total.toFixed(2)}`,
          duration: 6000,
        },
      );
      onAttached?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not attach processor");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="attach-processor-overlay"
    >
      <div
        className="nb-card p-5 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        data-testid="attach-processor-dialog"
      >
        <div className="flex justify-between items-start mb-3">
          <div>
            <div className="label-eyebrow flex items-center gap-1.5">
              <Wrench size={12} strokeWidth={2.5} /> Toll processing
            </div>
            <h3 className="font-display text-2xl">Attach to {po.po_number}</h3>
          </div>
          <button onClick={onClose} className="nb-btn bg-white p-2">
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>

        <div className="text-xs text-[#607087] mb-3">
          Creates a linked <b>service-request PO</b> to the processor for the
          cutting job. They'll receive their own PO number and email —
          separate spend, no invoice on {po.supplier_snapshot?.name}.
        </div>

        <div className="flex flex-col gap-3">
          <label>
            <div className="label-eyebrow mb-1">Processor *</div>
            <select
              data-testid="attach-processor-select"
              className="nb-input"
              value={processorId}
              onChange={(e) => setProcessorId(e.target.value)}
            >
              <option value="">— pick one —</option>
              {(processors || []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            {(processors || []).length === 0 && (
              <div className="text-[11px] text-[#8A1F1F] mt-1">
                No processors set up yet — flag a supplier as Processor on
                the Suppliers page first.
              </div>
            )}
          </label>

          <label>
            <div className="label-eyebrow mb-1">Fee per log ($) *</div>
            <input
              data-testid="attach-processor-fee"
              type="number"
              step="0.01"
              min="0"
              className="nb-input font-mono-num"
              value={feePerLog}
              onChange={(e) => setFeePerLog(e.target.value)}
              placeholder="e.g. 25.00"
            />
          </label>

          <div className="border-2 border-black rounded-xl p-3 bg-[#FFF8E1] text-sm">
            <div className="label-eyebrow">Preview</div>
            <div className="mt-1 flex flex-col gap-0.5">
              <div>
                Logs on this PO:{" "}
                <b className="font-mono-num">{totalLogs}</b>
              </div>
              <div>
                Wheels expected:{" "}
                <b className="font-mono-num">
                  {preview ? preview.totalWheels : "—"}
                </b>{" "}
                <span className="text-[#7A5300] text-xs">(10 per log)</span>
              </div>
              <div className="mt-1 pt-1 border-t border-[#E5A72E]">
                Cutting total:{" "}
                <b className="font-mono-num">
                  {preview ? `$${preview.totalCost.toFixed(2)}` : "—"}
                </b>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <button
            data-testid="attach-processor-cancel"
            onClick={onClose}
            disabled={busy}
            className="nb-btn bg-white px-4 py-2"
          >
            Cancel
          </button>
          <button
            data-testid="attach-processor-save"
            onClick={submit}
            disabled={busy}
            className="nb-btn nb-btn-primary px-4 py-2 flex items-center gap-1.5"
          >
            <Wrench size={14} strokeWidth={2.5} /> Attach & create companion
          </button>
        </div>
      </div>
    </div>
  );
};

export default AttachProcessorDialog;
